import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import type { CatalogSyncResult } from '@/api/console';
import styles from './CatalogSyncResultNotice.module.css';

/**
 * EAA-1817 (design frame 55d): what a catalog sync just did, as a dismissible
 * notice pinned bottom-right, with the two follow-ups the frame offers.
 *
 * WHY IT IS NOT `useToast()`. The frame's notice carries two ACTIONS ("Xem danh
 * mục", "Xử lý vai trò đã mất"), and the shared `ToastApi` has no slot for one —
 * the same reason the role-map undo banner is its own element (see
 * `useRoleMapWrites`'s note on `undoPending`). It also must not auto-dismiss: a
 * vanished role is a broken link somebody has to go and fix, and a message that
 * disappears after four seconds is a message that did not arrive.
 *
 * WHY THE COUNTS COME FROM THE SERVER. `added`/`deactivated` are fields on the
 * live sync result (EAA-1817 added them). Diffing catalogs client-side would mean
 * fetching the catalog twice around the write and hoping the app did not change
 * in between — and the second read can legitimately disagree with the first.
 *
 * A sync that changed NOTHING still renders: "16 roles, nothing moved" is the
 * answer to "did my click do anything", and silence is not.
 */
export function CatalogSyncResultNotice({
    result,
    appName,
    onViewCatalog,
    onHandleLostRoles,
    onDismiss,
}: {
    result: CatalogSyncResult;
    appName: string;
    onViewCatalog: () => void;
    /** Omitted when nothing was deactivated — there is nothing to go and fix. */
    onHandleLostRoles?: () => void;
    onDismiss: () => void;
}) {
    const { t } = useTranslation();

    const added = result.added ?? [];
    const deactivated = result.deactivated ?? [];

    return (
        <div className={styles.notice} role="status" aria-live="polite">
            <span className={styles.icon} aria-hidden="true">
                <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.4}
                >
                    <path d="m5 12 5 5 9-10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>

            <span className={styles.body}>
                <strong className={styles.title}>
                    {t('console.roleCoverage.syncNotice.title', { appName })}
                </strong>

                <span className={styles.text}>
                    {t('console.roleCoverage.syncNotice.total', {
                        count: result.active_roles ?? 0,
                    })}
                    {added.length > 0 && (
                        <>
                            {' · '}
                            <strong className={styles.added}>
                                {t('console.roleCoverage.syncNotice.added', {
                                    count: added.length,
                                })}
                            </strong>
                            {` (${added.join(', ')})`}
                        </>
                    )}
                    {deactivated.length > 0 && (
                        <>
                            {' · '}
                            <strong className={styles.lost}>
                                {t('console.roleCoverage.syncNotice.deactivated', {
                                    count: deactivated.length,
                                })}
                            </strong>
                            {` (${deactivated.join(', ')})`}
                        </>
                    )}
                    {added.length === 0 && deactivated.length === 0 && (
                        <>{` · ${t('console.roleCoverage.syncNotice.noChange')}`}</>
                    )}
                </span>

                <span className={styles.actions}>
                    <Button type="link" onClick={onViewCatalog}>
                        {t('console.roleCoverage.syncNotice.viewCatalog')}
                    </Button>
                    {/* Only offered when there IS something lost — a link that
                        leads to an empty list teaches the reader to ignore it. */}
                    {deactivated.length > 0 && onHandleLostRoles && (
                        <Button type="link" onClick={onHandleLostRoles}>
                            {t('console.roleCoverage.syncNotice.handleLost')}
                        </Button>
                    )}
                </span>
            </span>

            <Button className={styles.close} onClick={onDismiss} aria-label={t('common.close')}>
                ×
            </Button>
        </div>
    );
}

export default CatalogSyncResultNotice;
